// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { api } from "./scripts/api";

import {
  Database,
  LogOut,
} from "lucide-react";

import AppSidebar from './components/AppSidebar';
import SystemTray from './components/SystemTray';
import InventoryPage from './pages/InventoryPage';
import SoldPage from './pages/SoldPage';
import VinScanPage from './pages/VinScanPage';
import PdiPage from './pages/PdiPage';
import PdiSelectPage from './pages/PdiSelectPage';
import PdiTemplatePage from './pages/PdiTemplatePage';
import PdiReportsPage from './pages/PdiReportsPage';
import PdiManagePage from './pages/PdiManagePage';
import InventoryImportPage from './pages/InventoryImportPage';
import DriverPage from './pages/DriverPage';
import AdminPage from './pages/AdminPage';
import InventorySettingsPage from './pages/InventorySettingsPage';
import NewRecordPage from './pages/NewRecordPage';
import DetailPage from './pages/DetailPage';
import DemoPage from './pages/DemoPage';
import StockPage from './pages/StockPage';
import CustomerUnitsPage from './pages/CustomerUnitsPage';
import InventoryImportHubPage from './pages/InventoryImportHubPage';
import { loadAllUserPrefs, clearAllUserPrefs } from './scripts/userPrefs';
import RentalPage from './pages/RentalPage';
import BuilderUploadPage from "./pages/BuilderUploadPage";
import ReportsPage from "./pages/ReportsPage_old";
import AdminSchemaPage from "./pages/AdminSchemaPage";
import UsersPage from "./pages/UsersPage";
import DatabaseSettingsPage from "./pages/DatabaseSettingsPage";
import PreLoginSplashPage from "./pages/PreLoginSplashPage";
import MovesPage from "./pages/MovesPage";
import DriverMgmtPage from './pages/DriverMgmtPage';
import DriverCalendarPage from './pages/DriverCalendarPage';
import DriversTodayPage from './pages/DriversTodayPage';
import NotificationsPage from './pages/NotificationsPage';
import AdminExpensesPage from './pages/AdminExpensesPage';
import DriverStatsPage from './pages/DriverStatsPage';
import AdminDestinationsPage from './pages/AdminDestinationsPage';
import AdminDriverDocumentsPage from './pages/AdminDriverDocumentsPage';
import MasterSyncPage from './pages/MasterSyncPage';
import AdminCustomersPage from "./pages/AdminCustomersPage";
import AdminVendorsPage from "./pages/AdminVendorsPage";
import CustomerDetailsPage from "./pages/CustomerDetailsPage";
import AdminApiKeysPage from "./pages/AdminApiKeysPage";
import SchedulerPage from "./pages/SchedulerPage";
import DeveloperToolsPage from "./pages/DeveloperToolsPage";
import DriverPortalPage from "./pages/DriverPortalPage";
import PgPreviewPage from "./pages/PgPreviewPage";
import PdiPrintPage from "./pages/PdiPrintPage";
import HomePage from "./pages/HomePage";
import TripCostCalculatorPage from "./pages/TripCostCalculatorPage";
import ReportBuilderPage from "./pages/ReportBuilderPage";

export const ThemeCtx = React.createContext({ theme: "system", setTheme: () => {} });

function MaintenanceScreen({ message }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "var(--bg, #0f1117)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div className="card" style={{ maxWidth: 480, textAlign: "center", padding: "48px 40px" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔧</div>
        <div style={{ fontWeight: 900, fontSize: "1.4rem", marginBottom: 12 }}>
          System Maintenance
        </div>
        <div className="muted" style={{ lineHeight: 1.6 }}>
          {message || "The system is currently undergoing maintenance. Please check back shortly."}
        </div>
        <div className="muted" style={{ marginTop: 24, fontSize: "0.8rem" }}>
          Contact your administrator if this persists.
        </div>
      </div>
    </div>
  );
}

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("sna_theme") || "system");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    localStorage.setItem("sna_theme", theme);
  }, [theme]);

  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}

function hasToken() {
const t = sessionStorage.getItem("auth_token")?.trim() || "";
return !!t;
}

function hasDbSelected() {
  const d = sessionStorage.getItem("active_db") || "";
  return !!d;
}

function AuthGate({ children }) {
  const loc = useLocation();
  const nav = useNavigate();

  const path = loc.pathname;

  const tokenOk = hasToken();
  const dbOk = hasDbSelected();

  const isPublic =
    path === "/" ||
    path === "/db-select" ||
    path === "/driver-portal";

  // If not public, enforce token + db
  useEffect(() => {
    if (isPublic) return;

    if (!tokenOk) {
      nav("/", { replace: true });
      return;
    }
    if (!dbOk) {
      nav("/db-select", { replace: true });
      return;
    }
  }, [isPublic, tokenOk, dbOk, nav]);

  // Warm the user-preferences cache as soon as auth is established, so things
  // like the detail-page group order are ready by the time any page mounts.
  // When the token disappears (logout / 401), wipe the local cache so the
  // next user doesn't inherit prior preferences.
  useEffect(() => {
    if (tokenOk) {
      loadAllUserPrefs();
    } else {
      clearAllUserPrefs();
    }
  }, [tokenOk]);

  // 🚫 Block protected UI from rendering until checks pass
  if (!isPublic) {
    if (!tokenOk) return null;
    if (!dbOk) return null;
  }

  return children;
}



function getAuthUser() {
  try { return JSON.parse(sessionStorage.getItem("auth_user") || "null"); }
  catch { return null; }
}

function DbSelectPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState("");

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/db/list");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to list databases");
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const choose = (dbId) => {
    sessionStorage.setItem("active_db", dbId);
    nav("/inventory", { replace: true });
  };

  const createDb = async () => {
    const name = String(newName || "").trim();
    if (!name) return;

    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/db/create", {
        name,
        template: "inventory",
        extras: {
          finance: true,
          purchase_orders: true,
          tracking: true,
          date_tracking: true,
        },
      });
      const dbId = data?.dbId;
      if (dbId) {
        sessionStorage.setItem("active_db", dbId);
        nav("/inventory", { replace: true });
      } else {
        setMsg("Created DB but did not get dbId back");
        load();
      }
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to create database");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 0.2 }}>Select Database</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Your requests will target this DB (sent via <code>X-DB</code> header).
          </div>
        </div>

        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn-outline"
            onClick={() => {
              sessionStorage.removeItem("auth_token");
              sessionStorage.removeItem("active_db");
              nav("/", { replace: true });
            }}
          >
            <LogOut size={16} style={{ marginRight: 8 }} />
            Logout
          </button>
        </div>
      </div>

      {msg ? <div className="muted" style={{ marginTop: 10 }}>{msg}</div> : null}

      <div className="card" style={{ marginTop: 12, minWidth: 0 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 900 }}>Create new DB</div>
          <Database size={18} />
        </div>

        <div className="row" style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}>
          <input
            className="input"
            placeholder="Database name (e.g. SNA 2026)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <button className="btn" onClick={createDb} disabled={busy || !newName.trim()}>
            Create
          </button>
          <button className="btn-outline" onClick={load} disabled={busy}>
            Refresh
          </button>
        </div>

        <div className="muted" style={{ marginTop: 8 }}>
          Template: inventory + finance + PO + tracking + date tracking packs enabled by default.
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, minWidth: 0 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Available databases</div>

        {busy ? <div className="muted">Loading…</div> : null}
        {!busy && rows.length === 0 ? <div className="muted">No databases found.</div> : null}

        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {rows.map((r) => (
            <button key={r.dbId} className="btn" onClick={() => choose(r.dbId)}>
              Open: {r.name} <span className="muted" style={{ marginLeft: 6 }}>({r.dbId})</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}



function WithNav({ children }) {
  const loc = useLocation();
  const hide = loc.pathname === "/" || loc.pathname === "/db-select" || loc.pathname === "/driver-portal" || loc.pathname.startsWith("/pdi/print");

  if (hide) return children;

  return (
    <div className="app-shell">
      <AppSidebar />
      <main className="app-shell-main">
        {children}
      </main>
      <SystemTray />
    </div>
  );
}

export default function App() {
  const [maintenance, setMaintenance] = useState(() => window.__MAINTENANCE__ || null);

  useEffect(() => {
    const handler = (e) => setMaintenance(e.detail || "System maintenance in progress.");
    window.addEventListener("maintenance", handler);
    return () => window.removeEventListener("maintenance", handler);
  }, []);

  if (maintenance) return <MaintenanceScreen message={maintenance} />;

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthGate>
          <WithNav>
            <Routes>
              <Route path="/" element={<PreLoginSplashPage />} />  
              <Route path="/home" element={<HomePage />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/db-select" element={<DbSelectPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/rentals" element={<RentalPage />} />
              <Route path="/stock" element={<StockPage />} />
              <Route path="/customer-units" element={<CustomerUnitsPage />} />
              <Route path="/sold" element={<SoldPage />} />
          <Route path="/vin" element={<VinScanPage />} />
          <Route path="/pdi/print/:id" element={<PdiPrintPage />} />
          <Route path="/pdi/:id" element={<PdiPage />} />
          <Route path="/pdi/templates" element={<PdiTemplatePage />} />
          <Route path="/pdi/reports" element={<PdiReportsPage />} />
          <Route path="/pdi/manage" element={<PdiManagePage />} />
          <Route path="/pdi" element={<PdiSelectPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/inventory-settings" element={<InventorySettingsPage />} />
              <Route path="/new" element={<NewRecordPage />} />
              <Route path="/builder-upload" element={<BuilderUploadPage />} />
              <Route path="/detail/:table/:id" element={<DetailPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/admin/import" element={<InventoryImportHubPage />} />
              <Route path="/admin/import/inventory" element={<InventoryImportPage />} />
              <Route path="/admin/import/labrie" element={<BuilderUploadPage />} />
              <Route path="/admin/schema" element={<AdminSchemaPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/dbs" element={<DatabaseSettingsPage />} />
              <Route path="/admin/devtools" element={<DeveloperToolsPage />} />
              <Route path="/admin/pg-preview" element={<PgPreviewPage />} />
              <Route path="/admin/moves" element={<MovesPage />} />
              <Route path="/admin/driver-mgmt" element={<DriverMgmtPage />} />
              <Route path="/admin/driver-calendar" element={<DriverCalendarPage />} />
              <Route path="/admin/drivers-today" element={<DriversTodayPage />} />
              <Route path="/admin/notifications" element={<NotificationsPage />} />
              <Route path="/admin/expenses" element={<AdminExpensesPage />} />
              <Route path="/admin/driver-stats" element={<DriverStatsPage />} />
              <Route path="/admin/destinations" element={<AdminDestinationsPage />} />
              <Route path="/admin/driver-documents" element={<AdminDriverDocumentsPage />} />
              <Route path="/admin/master-sync" element={<MasterSyncPage />} />
              <Route path="/admin/customers" element={<AdminCustomersPage />} />
              <Route path="/admin/vendors" element={<AdminVendorsPage />} />
              <Route path="/admin/customers/details" element={<CustomerDetailsPage />} />
              <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
              <Route path="/admin/trip-cost" element={<TripCostCalculatorPage />} />
              <Route path="/admin/reports" element={<ReportBuilderPage />} />
              <Route path="/driver-portal" element={<DriverPortalPage />} />
              <Route path="/scheduler" element={<SchedulerPage />} />
              <Route path="*" element={<div className="container">Route Not found.</div>} />
            </Routes>
          </WithNav>
        </AuthGate>
      </ThemeProvider>
    </BrowserRouter>
  );
}
